import { CxcOptions, CxcRegistration, CxcSubject, CxcSubjectChoice } from '../types';

/**
 * The Cayman Academy (CA) CSEC registration form, as the paper form prints it.
 * Everything here is pure so the screens (native and web) share one set of
 * rules and the rules can be unit-tested without rendering anything.
 *
 * Sittings, deadlines and fees are NOT compiled into the app: they come from
 * the server (GET /CxcSettings/options?form=CA - see useCxcOptions), the one
 * copy staff edit and the one the server checks every submission against. So
 * a new year, a moved deadline or a new price needs no app release, and
 * "open" is decided by the server in Cayman time - no date is parsed here.
 */

export type CxcSittingKey = 'January' | 'MayJune';

export interface CxcSittingView {
  /**
   * "January-2027": what the form stores as the chosen sitting. The month
   * alone is not unique - this year's and next year's January sitting can be
   * open together, and matching on the month picks the wrong one.
   */
  id: string;
  /** The month the API expects (sittingMonth). */
  key: CxcSittingKey;
  label: string;
  year: number;
  /** "Friday, 09 October 2026", as the server words it. */
  deadline: string;
  isOpen: boolean;
  daysRemaining: number;
}

export interface CxcFeesView {
  centre: number;
  registration: number;
  processing: number;
  subject: number;
  practical: number;
  late: number;
  /** Optional cap on subjects; null = only the subjects on offer limit it. */
  maxSubjects: number | null;
}

export const CXC_SBA_FAQ_URL = 'https://www.cxc.org/reformed-cxc-sba-framework-faqs/';

/** The sittings the server offers, soonest deadline first, each marked open or closed. */
export type IdentificationType = 'NATIONAL_ID' | 'DRIVING_LICENSE' | 'PASSPORT';

/** The documents an applicant may upload as proof of identification, and what each one's number is called. */
export const IDENTIFICATION_TYPES: { value: IdentificationType; label: string; numberLabel: string }[] = [
  { value: 'NATIONAL_ID', label: 'National ID', numberLabel: 'National ID number' },
  { value: 'DRIVING_LICENSE', label: "Driving License", numberLabel: "Driving License number" },
  { value: 'PASSPORT', label: 'Passport', numberLabel: 'Passport number' },
];

export interface IdentificationInput {
  type: IdentificationType | '';
  number: string;
  /** Country of issue. Asked for a passport only. */
  country: string;
  /** Optional; held as typed, YYYY-MM-DD or DD/MM/YYYY. */
  expiry: string;
  hasDocument: boolean;
}

export const EMPTY_IDENTIFICATION: Omit<IdentificationInput, 'hasDocument'> = { type: '', number: '', country: '', expiry: '' };

/*
 * What a document number, a country of issue and an expiry date must look like. The server applies the same
 * rules and is the one that decides; these only tell the applicant early and keep stray characters out of the
 * box while they type. Deliberately country-neutral: a passport number is 6 to 9 letters or digits (the
 * international form); a National ID or Driving License number is 5 to 20 letters, digits or hyphens.
 */
const PASSPORT_NUMBER = /^[A-Z0-9]{6,9}$/;
const CARD_NUMBER = /^[A-Z0-9][A-Z0-9-]{3,18}[A-Z0-9]$/;
const COUNTRY_NAME = /^\p{L}[\p{L} .'-]{1,99}$/u;
/** Further ahead than this is a typing mistake. */
const MAX_EXPIRY_YEARS_AHEAD = 25;

/** Upper case, and only the characters that document's number can contain. */
export function sanitizeIdentificationNumber(type: IdentificationType | '', raw: string): string {
  const upper = raw.toUpperCase();
  const cleaned = type === 'PASSPORT' ? upper.replace(/[^A-Z0-9]/g, '') : upper.replace(/[^A-Z0-9-]/g, '');
  // Capped well above any number, never at the passport limit: a pasted 10-character passport number must be
  // refused as too long, not silently cut to 9 characters that then look valid.
  return cleaned.slice(0, 20);
}

/** Letters, spaces, full stops, apostrophes and hyphens only. */
export function sanitizeCountry(raw: string): string {
  return raw.replace(/[^\p{L} .'-]/gu, '').slice(0, 100);
}

export function identificationNumberError(type: IdentificationType, raw: string, label: string): string | null {
  const number = sanitizeIdentificationNumber(type, raw.replace(/\s/g, ''));
  if (!number) return `${label} is required`;
  if (type === 'PASSPORT') return PASSPORT_NUMBER.test(number) ? null : `${label} must be 6 to 9 letters or digits`;
  return CARD_NUMBER.test(number) ? null : `${label} must be 5 to 20 letters, digits or hyphens`;
}

export function identificationCountryError(raw: string): string | null {
  const country = raw.trim();
  if (!country) return 'The country of issue is required for a passport';
  return COUNTRY_NAME.test(country) ? null : 'The country of issue must contain letters only';
}

/** Optional: blank is fine. When typed it must be a real date that has not passed and is not absurdly far off. */
export function identificationExpiryError(raw: string, now: Date = new Date()): string | null {
  if (!raw.trim()) return null;
  const iso = normalizeDateInput(raw);
  if (!iso) return 'Enter the expiry date as DD/MM/YYYY';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (date < today) return 'This document has expired. Please upload a valid, unexpired document';
  const limit = new Date(today.getFullYear() + MAX_EXPIRY_YEARS_AHEAD, today.getMonth(), today.getDate());
  if (date > limit) return 'The expiry date is not valid';
  return null;
}

/** "Jayden_Miller_Test_Exam (1).pdf" -> ["Jayden_Miller_Test_Exam (1)", ".pdf"], so a long name can give way while the extension stays. */
export function splitFileName(name: string): [string, string] {
  const dot = name.lastIndexOf('.');
  return dot > 0 && name.length - dot <= 6 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
}

/** The size of the file a data URL holds, as people read it: "1.2 MB". */
export function dataUrlSizeLabel(dataUrl: string): string {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const bytes = Math.floor((base64.length * 3) / 4) - (base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The short name of the kind of file a data URL holds: "PDF", "JPG", "PNG" or "WEBP". */
export function identificationKind(dataUrl: string): string {
  const type = dataUrl.slice(5, dataUrl.indexOf(';'));
  if (type === 'application/pdf') return 'PDF';
  return type === 'image/jpeg' ? 'JPG' : type.replace('image/', '').toUpperCase();
}

/** yyyy-mm-dd of today, for a date box's min attribute. */
export function todayIsoDate(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * The identification document and the details of the chosen document. Its own function (not part of
 * validateCxcForm) so the rest of the form's validation is untouched; the screens merge the two results.
 */
export function validateIdentification(input: IdentificationInput, now: Date = new Date()): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!input.type) {
    errors.identificationType = 'Please choose the type of identification document';
  } else {
    const label = IDENTIFICATION_TYPES.find(t => t.value === input.type)?.numberLabel ?? 'The document number';
    const numberProblem = identificationNumberError(input.type, input.number, label);
    if (numberProblem) errors.identificationNumber = numberProblem;
    if (input.type === 'PASSPORT') {
      const countryProblem = identificationCountryError(input.country);
      if (countryProblem) errors.identificationCountry = countryProblem;
    }
  }
  const expiryProblem = identificationExpiryError(input.expiry, now);
  if (expiryProblem) errors.identificationExpiry = expiryProblem;
  if (!input.hasDocument) errors.identificationDocument = 'Please upload the identification document';
  return errors;
}

/** What the server accepts - it checks the file's own bytes, so the app only tells the person early. */
export const IDENTIFICATION_MAX_BYTES = 5 * 1024 * 1024;
export const IDENTIFICATION_PDF_PREFIX = 'data:application/pdf';

/**
 * Whether an image looks like a passport-size portrait rather than a photographed document: small and no
 * wider than it is tall. Used for a gentle warning only - never to refuse an upload, because a cropped scan
 * of a real passport page can look the same.
 */
export const looksLikePortraitPhoto = (width: number, height: number): boolean =>
  width > 0 && height > 0 && width < 600 && height >= width;

/** Shown beside the identification document when the image looks like a portrait photo. */
export const PORTRAIT_PHOTO_WARNING =
  'This looks like a portrait photo. Please make sure you upload the full identification document, not only a photograph.';

export function cxcSittings(options: CxcOptions | null | undefined): CxcSittingView[] {
  return (options?.sittings ?? []).map(s => ({
    // The server only ever sends these two (a CHECK constraint on skl.CxcSitting).
    id: `${s.sittingMonth}-${s.sittingYear}`,
    key: s.sittingMonth as CxcSittingKey,
    label: s.label,
    year: s.sittingYear,
    deadline: s.deadlineDisplay,
    isOpen: s.isOpen,
    daysRemaining: s.daysRemaining,
  }));
}

/** The fee table; all zero until the options arrive (screens wait for them before showing prices). */
const SHORT_DAYS: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

/**
 * "Tuesday, 29 September 2026" (the server's wording) as "Tue, 29 Sep 2026", so a deadline
 * fits one line on the Home card. Anything in another shape is returned as it came.
 */
export function shortCxcDeadline(display: string): string {
  const m = /^\s*([A-Za-z]+),\s*0?(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})\s*$/.exec(display ?? '');
  if (!m) return display;
  const day = SHORT_DAYS[m[1].toLowerCase()];
  if (!day) return display;
  return `${day}, ${Number(m[2])} ${m[3].slice(0, 3)} ${m[4]}`;
}

export function cxcFees(options: CxcOptions | null | undefined): CxcFeesView {
  const f = options?.fees;
  return {
    centre: f?.centreFee ?? 0,
    registration: f?.registrationFee ?? 0,
    processing: f?.processingFee ?? 0,
    subject: f?.subjectFee ?? 0,
    practical: f?.practicalFee ?? 0,
    late: f?.lateFee ?? 0,
    maxSubjects: f?.maxSubjects ?? null,
  };
}

export function cxcCentre(options: CxcOptions | null | undefined): { name: string; number: string } {
  return { name: options?.centreName ?? 'Cayman Academy', number: options?.centreNumber ?? '060103' };
}

/** The soonest sitting still open, or null once every sitting has closed. */
export function nextOpenCxcSitting(sittings: CxcSittingView[]): CxcSittingView | null {
  return sittings.find(s => s.isOpen) ?? null;
}

/**
 * True while at least one sitting can still be registered for. Menus, the
 * dashboard banner and the sidebar's highlight all key off this, so the
 * feature disappears on its own once every sitting has closed - nobody has
 * to remember to hide it by hand. False until the server has answered.
 */
export function isCxcRegistrationOpen(sittings: CxcSittingView[]): boolean {
  return sittings.some(s => s.isOpen);
}

/** True if that sitting (by its id) can still be registered for, as the server sees it. */
export function isSittingOpen(sittings: CxcSittingView[], sittingId: string): boolean {
  return sittings.some(s => s.id === sittingId && s.isOpen);
}

export interface CxcFormState {
  /** The chosen sitting's id ("January-2027"), or '' before one is chosen. */
  sitting: string;
  gradeOrClass: string;
  firstName: string;
  middleName: string;
  lastName: string;
  previousRegistrationNumber: string;
  /** Held as typed; normalised to YYYY-MM-DD at submit time (see normalizeDateInput). */
  dateOfBirth: string;
  gender: 'Female' | 'Male' | '';
  address: string;
  homePhone: string;
  cellPhone: string;
  email: string;
  parentGuardianName: string;
}

export const EMPTY_CXC_FORM: CxcFormState = {
  sitting: '',
  gradeOrClass: '',
  firstName: '',
  middleName: '',
  lastName: '',
  previousRegistrationNumber: '',
  dateOfBirth: '',
  gender: '',
  address: '',
  homePhone: '',
  cellPhone: '',
  email: '',
  parentGuardianName: '',
};

/** Per-subject choice keyed by subject name: ticked, and whether it is a re-sit (SBA reuse). */
export type CxcSubjectSelection = Record<string, { selected: boolean; resit: boolean }>;

export interface CxcInvoiceLine {
  code: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export function sittingLabel(sittings: CxcSittingView[], key: CxcSittingKey | string): string {
  return sittings.find(s => s.id === key)?.label ?? '';
}

/**
 * What a family reads on a past registration: its invoice is still being
 * raised, it is waiting for payment, paid, or entered with CXC.
 */
export function cxcStatusLabel(r: Pick<CxcRegistration, 'status' | 'invoicePending'>): string {
  if (r.invoicePending) return 'Invoice pending';
  switch (r.status) {
    case 'RegisteredWithCxc': return 'Registered with CXC';
    case 'Paid': return 'Paid';
    case 'Submitted': return 'Awaiting payment';
    default: return r.status || '-';
  }
}

export function formatMoney(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

// Practical and late fees are assessed by the office in person, so they are
// shown at their rate but never added to the estimate - the paper form leaves
// TOTAL COST blank for the same reason.
export function estimateCxcTotal(fees: CxcFeesView, subjectCount: number): number {
  return fees.centre + fees.registration + fees.processing + subjectCount * fees.subject;
}

export function buildCxcInvoiceLines(fees: CxcFeesView, subjectCount: number): CxcInvoiceLine[] {
  return [
    { code: 'CENTRE_FEE', description: 'Centre Fee (one-off)', quantity: 1, unitPrice: fees.centre, amount: fees.centre },
    { code: 'REGISTRATION_FEE', description: 'Registration Fee (one-off)', quantity: 1, unitPrice: fees.registration, amount: fees.registration },
    { code: 'PROCESSING_FEE', description: 'Processing Fee (one-off)', quantity: 1, unitPrice: fees.processing, amount: fees.processing },
    {
      code: 'SUBJECT_FEE',
      description: `Subject Fee (${subjectCount} × ${formatMoney(fees.subject)})`,
      quantity: subjectCount,
      unitPrice: fees.subject,
      amount: subjectCount * fees.subject,
    },
    { code: 'PRACTICAL_FEE', description: `Practical Fee (${formatMoney(fees.practical)} per practical - assessed by the office)`, quantity: 0, unitPrice: fees.practical, amount: 0 },
    { code: 'LATE_FEE', description: `Late Fee (${formatMoney(fees.late)} if applicable - assessed by the office)`, quantity: 0, unitPrice: fees.late, amount: 0 },
  ];
}

/** Turns the selection map back into the API's subject list, in the grid's order. */
export function selectedSubjectChoices(subjects: CxcSubject[], selection: CxcSubjectSelection): CxcSubjectChoice[] {
  return subjects
    .filter(s => selection[s.name]?.selected)
    .map(s => ({ subjectName: s.name, proficiency: s.proficiency, isResit: !!selection[s.name]?.resit }));
}

/** Tick/untick, or toggle re-sit. Re-sit only makes sense once the subject itself is chosen. */
export function toggleSubject(
  selection: CxcSubjectSelection,
  name: string,
  field: 'selected' | 'resit',
): CxcSubjectSelection {
  const current = selection[name] ?? { selected: false, resit: false };
  const next = { ...current, [field]: !current[field] };
  if (field === 'selected' && !next.selected) next.resit = false;
  if (field === 'resit' && next.resit) next.selected = true;
  return { ...selection, [name]: next };
}

/**
 * Accepts the API's YYYY-MM-DD, or the paper form's DD/MM/YYYY as a parent would
 * type it, and returns YYYY-MM-DD - or null when it is not a real calendar date.
 */
export function normalizeDateInput(raw: string): string | null {
  const value = (raw ?? '').trim();
  let y: number;
  let m: number;
  let d: number;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
  const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (iso) {
    [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  } else if (dmy) {
    [d, m, y] = [Number(dmy[1]), Number(dmy[2]), Number(dmy[3])];
  } else {
    return null;
  }

  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;

  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

const pick = (obj: any, ...keys: string[]): any => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') return value;
  }
  return undefined;
};

/**
 * Pre-fills the form from the /Parent snapshot the app already holds for the
 * pupil (the same one the profile screen reads, in either key casing) - names
 * exactly as the school records them, DOB, gender, class, contact details -
 * falling back to the parent's own email/phone where the pupil has none.
 * Only fields with a value are returned, so nothing already typed is blanked.
 */
export function prefillFromSnapshot(
  student: { name?: string; class?: string; address1?: string; address2?: string; rawSnapshot?: any } | null | undefined,
): Partial<CxcFormState> {
  if (!student) return {};

  const raw = student.rawSnapshot ?? {};
  const profile = pick(raw, 'Profile', 'profile') ?? {};
  const relatives: any[] = pick(raw, 'Relatives', 'relatives') ?? [];
  const parent = relatives.find(r => pick(r, 'UserId', 'userId')) ?? relatives[0];

  const nameParts = (student.name ?? '').trim().split(/\s+/).filter(Boolean);
  const gender = String(pick(profile, 'Gender', 'gender') ?? '').trim().toUpperCase();
  const dob = String(pick(profile, 'DateOfBirth', 'dateOfBirth') ?? '').slice(0, 10);
  const address = [pick(profile, 'Address1', 'address1') ?? student.address1, pick(profile, 'Address2', 'address2') ?? student.address2]
    .filter(Boolean)
    .join(', ');
  const parentName = parent ? [pick(parent, 'FirstName', 'firstName'), pick(parent, 'LastName', 'lastName')].filter(Boolean).join(' ') : '';

  const filled: Partial<CxcFormState> = {
    firstName: pick(profile, 'FirstName', 'firstName') ?? nameParts[0],
    middleName: pick(profile, 'MiddleName', 'middleName') ?? (nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : undefined),
    lastName: pick(profile, 'LastName', 'lastName') ?? (nameParts.length > 1 ? nameParts[nameParts.length - 1] : undefined),
    dateOfBirth: dob || undefined,
    gender: gender === 'M' || gender === 'MALE' ? 'Male' : gender === 'F' || gender === 'FEMALE' ? 'Female' : undefined,
    email: pick(profile, 'StudentEmail', 'studentEmail') ?? pick(parent, 'Email', 'email'),
    cellPhone: pick(profile, 'StudentPhone', 'studentPhone') ?? pick(parent, 'Phone', 'phone'),
    address: address || pick(parent, 'Address', 'address'),
    gradeOrClass: student.class,
    parentGuardianName: parentName || undefined,
  };

  return Object.fromEntries(
    Object.entries(filled).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== ''),
  ) as Partial<CxcFormState>;
}

export function candidateFullName(form: Pick<CxcFormState, 'firstName' | 'middleName' | 'lastName'>): string {
  return [form.firstName, form.middleName, form.lastName].map(s => s.trim()).filter(Boolean).join(' ');
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface CxcValidationContext {
  hasStudent: boolean;
  selectedSubjectCount: number;
  hasPhoto: boolean;
  hasCandidateSignature: boolean;
  /** True when any chosen subject is ticked as a re-sit - the previous registration number is then required. */
  hasResit?: boolean;
  /** The sittings the server offers; the chosen one must still be open. */
  sittings?: CxcSittingView[];
  /** The optional subject cap from the fee settings. */
  maxSubjects?: number | null;
  /** The student's active class name or grade (optional). */
  studentClass?: string | null;
}

/**
 * Checks whether a class name or number is strictly above Year 6 (Year 7 and above).
 * CXC / CSEC / CAPE examinations are only taken by secondary school students.
 */
export function isClassAboveYear6(classNameOrNumber?: string | number | null): boolean {
  if (classNameOrNumber === undefined || classNameOrNumber === null) return false;
  if (typeof classNameOrNumber === 'number') {
    return classNameOrNumber > 6;
  }
  const str = String(classNameOrNumber).trim();
  if (!str) return false;

  if (/cape|sixth\s*form/i.test(str)) return true;

  const match = str.match(/(\d+)/);
  if (match) {
    const yearNum = parseInt(match[1], 10);
    return yearNum > 6;
  }
  return false;
}

/**
 * Checks whether the student is enrolled in a class above Year 6 (Year 7+),
 * making them eligible for CXC / CSEC registration.
 */
export function isStudentEligibleForCxc(student: any): boolean {
  if (!student) return false;
  if (isClassAboveYear6(student.class)) return true;

  const raw = student.rawSnapshot ?? {};
  const enrollments = raw.Enrollments ?? raw.enrollments ?? [];
  for (const e of enrollments) {
    if (e.IsActive ?? e.isActive) {
      if (
        isClassAboveYear6(e.ClassDisplayName ?? e.classDisplayName) ||
        isClassAboveYear6(e.ClassName ?? e.className) ||
        isClassAboveYear6(e.ClassNumber ?? e.classNumber)
      ) {
        return true;
      }
    }
  }
  return false;
}

/** Mirrors the backend's own guard clauses so a parent never waits on a round trip to learn the obvious. */
export function validateCxcForm(form: CxcFormState, ctx: CxcValidationContext): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!ctx.hasStudent) errors.student = 'Select which child this registration is for';
  if (!form.sitting) errors.sitting = 'Choose which sitting you are registering for';
  else if (ctx.sittings && !isSittingOpen(ctx.sittings, form.sitting)) {
    const closed = ctx.sittings.find(s => s.id === form.sitting);
    errors.sitting = closed ? `Registration for ${closed.label} closed on ${closed.deadline}` : 'Choose an open sitting';
  }
  if (!form.firstName.trim()) errors.firstName = 'First name is required';
  if (!form.lastName.trim()) errors.lastName = 'Last name is required';

  if (!form.dateOfBirth.trim()) {
    errors.dateOfBirth = 'Date of birth is required';
  } else if (!normalizeDateInput(form.dateOfBirth)) {
    errors.dateOfBirth = 'Enter the date of birth as DD/MM/YYYY';
  } else {
    const dob = new Date(`${normalizeDateInput(form.dateOfBirth)}T00:00:00`);
    if (dob > new Date()) errors.dateOfBirth = 'Date of birth cannot be in the future';
  }

  if (!form.gender) errors.gender = 'Select a gender';
  if (!form.address.trim()) errors.address = 'Residential address is required';
  if (!form.cellPhone.trim()) {
    errors.cellPhone = 'A cell number is required';
  } else if (form.cellPhone.replace(/[^0-9]/g, '').length < 7) {
    errors.cellPhone = 'Enter a valid cell number';
  }

  if (!form.email.trim()) {
    errors.email = 'Email address is required';
  } else if (!EMAIL_PATTERN.test(form.email.trim())) {
    errors.email = 'Enter a valid email address';
  }

  if (form.gradeOrClass.trim() && !isClassAboveYear6(form.gradeOrClass)) {
    errors.gradeOrClass = 'CXC registration is only available for Year 7 and above';
  } else if (ctx.studentClass && !isClassAboveYear6(ctx.studentClass)) {
    errors.gradeOrClass = 'CXC registration is only available for Year 7 and above';
  }

  // A re-sit re-uses an earlier grade, which the office finds by the old registration number.
  if (ctx.hasResit && !form.previousRegistrationNumber.trim()) {
    errors.previousRegistrationNumber = 'Previous registration number is required for a re-sit';
  }

  if (ctx.selectedSubjectCount === 0) errors.subjects = 'Select at least one subject';
  else if (ctx.maxSubjects && ctx.selectedSubjectCount > ctx.maxSubjects) errors.subjects = `Choose at most ${ctx.maxSubjects} subjects`;
  if (!ctx.hasPhoto) errors.photo = "A photo of the candidate is required";
  if (!ctx.hasCandidateSignature) errors.candidateSignature = "The candidate's signature is required";

  return errors;
}
